import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button } from "@/components/ui/PageHeader";
import { api } from "@/api/client";
import * as users from "@/api/users";

/**
 * Extended profile — onboarding steps 5–10 as OPTIONAL post-onboarding
 * sections (the wizard deliberately stops at heritage so sign-up stays
 * fast), plus the identity-board discoverability setting. Each section
 * saves independently via the step endpoints the backend always had.
 */

type FieldDef =
  | { kind: "select"; key: string; label: string; options: string[]; required?: boolean }
  | { kind: "text"; key: string; label: string; required?: boolean }
  | { kind: "number"; key: string; label: string }
  | { kind: "list"; key: string; label: string; hint?: string };

interface SectionDef {
  step: number;
  title: string;
  subtitle: string;
  fields: FieldDef[];
}

const SECTIONS: SectionDef[] = [
  {
    step: 5,
    title: "Cultural background",
    // No gotra (2026-10-15): it is caste-linked and the server no longer keeps it.
    // No typed community either (2026-10-18): a people is said through the roots and the people-names.
    subtitle: "Dietary preference (religion lives on the heritage step)",
    fields: [
      {
        kind: "select",
        key: "dietaryPreference",
        label: "Dietary preference",
        options: ["vegetarian", "vegan", "non_vegetarian", "jain_vegetarian", "eggetarian", "prefer_not_to_say"],
      },
    ],
  },
  {
    step: 6,
    title: "Professional background",
    subtitle: "Feeds professional matching and Business Connect later",
    fields: [
      {
        kind: "select",
        key: "employmentStatus",
        label: "Employment status",
        required: true,
        options: ["employed", "self_employed", "student", "looking_for_work", "retired", "homemaker", "prefer_not_to_say"],
      },
      { kind: "text", key: "industry", label: "Industry", required: true },
      { kind: "text", key: "jobTitle", label: "Job title" },
      {
        kind: "select",
        key: "yearsOfExperience",
        label: "Experience",
        required: true,
        options: ["zero_to_2_years", "two_to_5_years", "five_to_10_years", "ten_to_15_years", "fifteen_plus_years"],
      },
      { kind: "text", key: "companyName", label: "Company" },
    ],
  },
  {
    step: 7,
    title: "Immigration journey",
    subtitle: "Sensitive — stored in its own partition, feeds the journey block",
    fields: [
      {
        kind: "select",
        key: "visaStatus",
        label: "Visa status",
        required: true,
        options: [
          "student_f1", "work_h1b", "work_l1", "work_h4_ead", "green_card", "us_citizen", "opt", "cpt",
          "other_visa", "canadian_pr", "canadian_citizen", "uk_tier2", "uk_ilr", "uk_citizen",
          "australian_pr", "australian_citizen", "other", "prefer_not_to_say",
        ],
      },
      {
        kind: "select",
        key: "immigrationGoal",
        label: "Goal",
        required: true,
        options: ["study", "permanent_settlement", "work_experience", "business_investment", "family_reunion", "undecided", "prefer_not_to_say"],
      },
      { kind: "number", key: "yearsSinceAbroad", label: "Years abroad" },
      {
        kind: "select",
        key: "planningToReturn",
        label: "Planning to return?",
        options: ["within_5_years", "maybe_uncertain", "no_settled_here", "prefer_not_to_say"],
      },
    ],
  },
  {
    step: 8,
    title: "Family & social",
    subtitle: "What you're looking for on WeDesi",
    fields: [
      {
        kind: "select",
        key: "maritalStatus",
        label: "Marital status",
        options: ["single", "married", "divorced", "widowed", "separated", "prefer_not_to_say"],
      },
      {
        kind: "select",
        key: "hasChildren",
        label: "Children",
        options: ["no_children", "one_child", "two_children", "three_plus_children", "prefer_not_to_say"],
      },
      { kind: "list", key: "lookingFor", label: "Looking for", hint: "comma-separated, e.g. friends, networking" },
    ],
  },
  {
    step: 9,
    title: "Services & interests",
    subtitle: "Personalizes recommendations and the home feed",
    fields: [
      { kind: "list", key: "servicesNeeded", label: "Services needed", hint: "e.g. pandit, cpa, tiffin" },
      { kind: "list", key: "contentInterests", label: "Content interests", hint: "e.g. festivals, food" },
      { kind: "list", key: "interests", label: "Interests", hint: "e.g. cricket, bollywood" },
    ],
  },
  {
    step: 10,
    title: "Privacy",
    subtitle: "Who can see your profile and location",
    fields: [
      {
        kind: "select",
        key: "profileVisibility",
        label: "Profile visible to",
        options: ["everyone", "indians_only", "same_state_only", "connections_only"],
      },
      {
        kind: "select",
        key: "locationVisibility",
        label: "Location shown as",
        options: ["exact_city", "state_only", "country_only", "hidden"],
      },
    ],
  },
];

function humanize(v: string): string {
  return v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function ExtendedProfileLab() {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Extended Profile"
        subtitle="Optional profile depth — professional, immigration, family, services, privacy. Each section saves on its own."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Extended Profile" },
        ]}
      />
      {SECTIONS.map((s) => (
        <StepSection key={s.step} section={s} />
      ))}
      <BoardPrivacySection />
      <CalledNameSection />
    </div>
  );
}

function StepSection({ section }: { section: SectionDef }) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setValues((v) => ({ ...v, [key]: e.target.value }));

  async function save() {
    setBusy(true);
    setMsg(null);
    const body: Record<string, unknown> = {};
    for (const f of section.fields) {
      const raw = values[f.key];
      if (raw === undefined || raw === "") continue;
      if (f.kind === "number") body[f.key] = Number(raw);
      else if (f.kind === "list") body[f.key] = raw.split(",").map((s) => s.trim()).filter(Boolean);
      else body[f.key] = raw;
    }
    try {
      await api.patch(`/onboarding/step/${section.step}`, body);
      setMsg("Saved ✓");
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setBusy(false);
    }
  }

  const missingRequired = section.fields.some(
    (f) => "required" in f && f.required && !(values[f.key] ?? "").trim(),
  );

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5">
      <h2 className="text-lg font-semibold">{section.title}</h2>
      <p className="text-xs text-gray-500 mb-3">{section.subtitle}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {section.fields.map((f) => (
          <div key={f.key}>
            <label className="label">
              {f.label}
              {"required" in f && f.required ? " *" : ""}
            </label>
            {f.kind === "select" ? (
              <select className="input" value={values[f.key] ?? ""} onChange={set(f.key)}>
                <option value="">—</option>
                {f.options.map((o) => (
                  <option key={o} value={o}>{humanize(o)}</option>
                ))}
              </select>
            ) : (
              <input
                className="input"
                type={f.kind === "number" ? "number" : "text"}
                placeholder={f.kind === "list" ? (f as Extract<FieldDef, { kind: "list" }>).hint : undefined}
                value={values[f.key] ?? ""}
                onChange={set(f.key)}
              />
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 mt-3">
        <Button size="sm" isLoading={busy} disabled={missingRequired} onClick={save}>
          Save section
        </Button>
        {missingRequired && <span className="text-xs text-gray-400">Fill the * fields to save</span>}
        {msg && <span className="text-sm text-gray-600">{msg}</span>}
      </div>
    </section>
  );
}

function BoardPrivacySection() {
  const [discoverableBy, setDiscoverableBy] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    setMsg(null);
    try {
      await api.patch("/identity-board/settings", { discoverableBy });
      setMsg("Saved ✓");
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5">
      <h2 className="text-lg font-semibold">Identity board discoverability</h2>
      <p className="text-xs text-gray-500 mb-3">
        Who can find you in match discovery — separate from profile visibility above.
      </p>
      <div className="flex gap-3 items-end flex-wrap">
        <div>
          <label className="label">Discoverable by</label>
          <select className="input w-56" value={discoverableBy} onChange={(e) => setDiscoverableBy(e.target.value)}>
            <option value="">—</option>
            {["everyone", "women_only", "connections_only", "nobody"].map((o) => (
              <option key={o} value={o}>{humanize(o)}</option>
            ))}
          </select>
        </div>
        <Button size="sm" isLoading={busy} disabled={!discoverableBy} onClick={save}>
          Save
        </Button>
        {msg && <span className="text-sm text-gray-600">{msg}</span>}
      </div>
    </section>
  );
}

/**
 * What to call the member in a sentence ("Wave at Ramesh"), and the line under
 * their name as the server composes it. Both from GET /users/me; the called
 * name is saved with PATCH /users/me, and "" goes back to the rule from the name.
 */
function CalledNameSection() {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ["users", "me", "declaration"], queryFn: users.getMyDeclaration });
  const [calledName, setCalledName] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(body: Parameters<typeof users.updateMe>[0]) {
    setBusy(true);
    setMsg(null);
    try {
      await users.updateMe(body);
      await qc.invalidateQueries({ queryKey: ["users", "me", "declaration"] });
      setCalledName("");
      setMsg("Saved ✓");
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setBusy(false);
    }
  }

  const data = me.data?.data;
  const PARTS: Array<{ key: keyof users.PartVisibilitySet; label: string }> = [
    { key: "roots", label: "Roots" },
    { key: "grewUp", label: "Where you grew up" },
    { key: "languages", label: "Languages" },
  ];
  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5">
      <h2 className="text-lg font-semibold">What we call you</h2>
      <p className="text-xs text-gray-500 mb-1">
        Called <strong>{data?.calledName || "—"}</strong> in a sentence. The line under your name:{" "}
        <strong>{data?.declaration.line ?? "nothing is shown"}</strong>
        {data?.declaration.plates.length ? ` (plates ${data.declaration.plates.join(" ")})` : ""}
      </p>
      <p className="text-xs text-gray-500 mb-3">
        Under it: <strong>{data?.declaration.count?.label ?? "nothing (under three others)"}</strong>
      </p>
      {/* Who sees each part. A hidden part is left out of every response, never flagged. */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        {PARTS.map(({ key, label }) => (
          <label key={key} className="text-xs text-gray-600 flex items-center gap-1">
            {label}
            <select
              className="input py-1 text-xs"
              value={data?.visibility[key] ?? "everyone"}
              disabled={busy || !data}
              onChange={(e) => save({ visibility: { [key]: e.target.value as users.PartVisibility } })}
            >
              <option value="everyone">everyone</option>
              <option value="connections">connections</option>
              <option value="only_me">only me</option>
            </select>
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <input
          className="input max-w-xs"
          placeholder="What to call you"
          value={calledName}
          onChange={(e) => setCalledName(e.target.value)}
        />
        <Button size="sm" isLoading={busy} disabled={!calledName.trim()} onClick={() => save({ calledName })}>
          Save
        </Button>
        <Button size="sm" variant="secondary" disabled={busy} onClick={() => save({ calledName: "" })}>
          Use the rule from my name
        </Button>
        {msg && <span className="text-sm text-gray-600">{msg}</span>}
      </div>
    </section>
  );
}
